<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="mm-question">
        <span class="mm-bar mm-bar--md"></span>
      </div>
      <div class="mm-grid">
        <div
          v-for="(card, i) in cards"
          :key="i"
          class="mm-card"
          :class="{ '--flipped': card.flipped, '--match': card.match }"
          :style="{ '--card-i': i }"
        >
          <span class="mm-card-back"></span>
          <span class="mm-card-front">
            <span v-if="card.flipped" class="mm-card-icon" :style="{ background: card.color }"></span>
          </span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="mm-phone-q"></span>
      <div class="mm-phone-grid">
        <div
          v-for="(card, i) in phoneCards"
          :key="'p' + i"
          class="mm-phone-card"
          :class="{ '--flipping': i === 1 }"
        >
          <span v-if="card.flipped" class="mm-phone-icon" :style="{ background: card.color }"></span>
        </div>
      </div>
      <div class="mm-phone-hint">
        Tap to flip
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewMemoryMatch',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  computed: {
    cards() {
      return [
        { flipped: true, match: true, color: 'var(--accent)' },
        { flipped: false, match: false, color: '' },
        { flipped: false, match: false, color: '' },
        { flipped: true, match: true, color: 'var(--accent)' },
      ]
    },
    phoneCards() {
      return [
        { flipped: false, color: '' },
        { flipped: true, color: 'var(--accent)' },
        { flipped: false, color: '' },
        { flipped: false, color: '' },
      ]
    },
  },
})
</script>

<style scoped lang="scss">
.mm-bar {
  display: block; height: 7px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
}
.mm-bar--md { width: 60%; }

.mm-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.mm-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  flex: 1;
  max-width: 220px;
  margin: 0 auto;
}
.mm-card {
  position: relative;
  aspect-ratio: 1.4 / 1;
  border-radius: 8px;
  perspective: 600px;
}
.mm-card-back, .mm-card-front {
  position: absolute; inset: 0;
  border-radius: 8px;
  display: grid; place-items: center;
  backface-visibility: hidden;
  transition: transform 600ms ease;
}
.mm-card-back {
  background: rgba(15, 15, 20, 0.85);
}
.mm-card-back::after {
  content: '?';
  color: white;
  font-size: 18px; font-weight: 800;
  opacity: 0.5;
}
.mm-card-front {
  background: white;
  border: 2px solid color-mix(in srgb, var(--accent) 50%, transparent);
  transform: rotateY(180deg);
}
.mm-card.--flipped .mm-card-back { transform: rotateY(180deg); }
.mm-card.--flipped .mm-card-front { transform: rotateY(0deg); }
.mm-card.--match .mm-card-front {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, white);
  animation: mm-glow 4s ease-in-out infinite;
}
.mm-card-icon {
  width: 24px; height: 24px;
  border-radius: 999px;
}

/* ---- Audience phone ---- */
.mm-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.mm-phone-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}
.mm-phone-card {
  aspect-ratio: 1 / 1;
  background: rgba(15, 15, 20, 0.85);
  border-radius: 6px;
  display: grid; place-items: center;
}
.mm-phone-card::after {
  content: '?';
  color: white;
  font-size: 12px; font-weight: 800;
  opacity: 0.5;
}
.mm-phone-card.--flipping {
  background: white;
  border: 1.5px solid var(--accent);
  animation: mm-flip 4s ease-in-out infinite;
}
.mm-phone-card.--flipping::after { content: ''; }
.mm-phone-icon {
  width: 18px; height: 18px;
  border-radius: 999px;
}
.mm-phone-hint {
  font-size: 10px; font-weight: 700;
  color: var(--accent);
  text-align: center;
  margin-top: auto;
}

@keyframes mm-glow {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 30%, transparent); }
  50%      { box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 0%, transparent); }
}
@keyframes mm-flip {
  0%, 12%  { transform: rotateY(180deg); }
  35%, 80% { transform: rotateY(0deg); }
  92%, 100% { transform: rotateY(180deg); }
}

@media (prefers-reduced-motion: reduce) {
  .mm-card.--match .mm-card-front, .mm-phone-card.--flipping { animation: none; transform: none; }
}
</style>
